import { DataTag } from '@/components/DataTag/DataTag'
import { SectionCard } from '@/components/SectionCard/SectionCard'
import { toaster } from '@/components/ui/toaster'
import { hivesOverviewOrganizationDetailsRoute } from '@/features/HivesOverview/pages/HivesOverviewOrganizationDetails'
import {
  useActivateUser,
  useResendActivationEmail,
} from '@/generated/http-clients/v2ApiComponents'
import type { UserWithOrgs } from '@/generated/http-clients/v2ApiSchemas'
import {
  ERROR_TOAST_OPTION,
  SUCCESS_TOAST_OPTION,
} from '@/hooks/useToast/toastUtils'
import {
  Box,
  Button,
  Grid,
  GridItem,
  HStack,
  Table,
  Text,
} from '@chakra-ui/react'
import { useNavigate } from '@tanstack/react-router'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

enum AuthMethod {
  UsernameAndPassword = 'usernameAndPassword',
  Google = 'google',
  Github = 'github',
  LinkedIn = 'linkedin',
}

export const ACTIVATED_STATUS = 'ACTIVATED'

export function UserAccount({
  user,
  email,
}: Readonly<{ user: UserWithOrgs; email: string }>) {
  const { t } = useTranslation('features', { useSuspense: false })
  const navigate = useNavigate()

  const {
    mutate: activateUser,
    isPending: isActivatingUser,
    isSuccess: isUserActivatedSuccessfully,
  } = useActivateUser({
    onSuccess: () => {
      toaster.create({
        ...SUCCESS_TOAST_OPTION,
        title: t('accountManagement.account.toast.activateSuccess'),
      })
    },
    onError: () => {
      toaster.create({
        ...ERROR_TOAST_OPTION,
        title: t('accountManagement.account.toast.activateError'),
      })
    },
  })

  const {
    mutate: resendActivationEmail,
    isPending: isResendingActivationEmail,
    isSuccess: isActivationEmailResentSuccessfully,
  } = useResendActivationEmail({
    onSuccess: () => {
      toaster.create({
        ...SUCCESS_TOAST_OPTION,
        title: t('accountManagement.account.toast.resendSuccess'),
      })
    },
    onError: () => {
      toaster.create({
        ...ERROR_TOAST_OPTION,
        title: t('accountManagement.account.toast.resendError'),
      })
    },
  })

  const authMethod = getAuthMethodByUserId(user.id)
  const authMethodDisplayValue = getAuthMethodDisplayValue(authMethod, t)

  const isUserActive =
    user.activation_status === ACTIVATED_STATUS || isUserActivatedSuccessfully

  const showResendButton =
    !isUserActive && authMethod === AuthMethod.UsernameAndPassword

  return (
    <SectionCard key={user.id}>
      <HStack justifyContent="space-between" width="100%" mb={4}>
        <Text fontSize="xl" fontWeight={500}>
          {email.toUpperCase()}
        </Text>
        <HStack>
          {!isUserActive && (
            <Button
              onClick={() =>
                activateUser({
                  body: { userId: user.id },
                })
              }
              loading={isActivatingUser}
              colorPalette="green"
              size="sm"
            >
              {t('accountManagement.account.activateButton')}
            </Button>
          )}
          {showResendButton && (
            <Button
              onClick={() =>
                resendActivationEmail({
                  body: { userId: user.id },
                })
              }
              loading={isResendingActivationEmail}
              disabled={isActivationEmailResentSuccessfully}
              colorPalette="blue"
              size="sm"
            >
              {t('accountManagement.account.resendButton')}
            </Button>
          )}
        </HStack>
      </HStack>

      <Grid templateColumns="repeat(2, 1fr)" width="100%" gap={8}>
        <GridItem>
          <SectionCard
            title={t('accountManagement.account.title')}
            width="100%"
            height="100%"
            p={0}
          >
            <Table.Root>
              <Table.Body>
                <Table.Row>
                  <Table.Cell>
                    {t('accountManagement.account.userId')}
                  </Table.Cell>
                  <Table.Cell>
                    <DataTag value={user.id} fontWeight={500} fontSize="1rem">
                      {user.id}
                    </DataTag>
                  </Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>
                    {t('accountManagement.account.authMethod.label')}
                  </Table.Cell>
                  <Table.Cell>
                    <DataTag
                      value={authMethodDisplayValue}
                      fontWeight={500}
                      fontSize="1rem"
                      hasCopy={false}
                    >
                      {authMethodDisplayValue}
                    </DataTag>
                  </Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>
                    {t('accountManagement.account.activationStatus.label')}
                  </Table.Cell>
                  <Table.Cell>
                    <DataTag
                      value={
                        isUserActive
                          ? String(
                              t(
                                'accountManagement.account.activationStatus.activated',
                              ),
                            )
                          : String(
                              t(
                                'accountManagement.account.activationStatus.notActive',
                              ),
                            )
                      }
                      fontWeight={500}
                      fontSize="1rem"
                      hasCopy={false}
                      colorPalette={isUserActive ? 'green' : 'red'}
                    >
                      {isUserActive
                        ? t(
                            'accountManagement.account.activationStatus.activated',
                          )
                        : t(
                            'accountManagement.account.activationStatus.notActive',
                          )}
                    </DataTag>
                  </Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table.Root>
          </SectionCard>
        </GridItem>

        <GridItem>
          <SectionCard
            title={t('accountManagement.organizations.title')}
            width="100%"
            height="100%"
          >
            {user.orgs && user.orgs.length > 0 ? (
              <Table.Root>
                <Table.Body>
                  {user.orgs.map((org, index) => (
                    <React.Fragment key={org.org_id}>
                      {index > 0 && (
                        <Table.Row>
                          <Table.Cell colSpan={2}>
                            <Box borderTop="1px solid" borderColor="gray.200" />
                          </Table.Cell>
                        </Table.Row>
                      )}
                      <Table.Row>
                        <Table.Cell>
                          {t('accountManagement.organizations.orgId')}
                        </Table.Cell>
                        <Table.Cell>
                          <DataTag
                            fontWeight={500}
                            fontSize="1rem"
                            value={org.org_id}
                            hasCopy={true}
                            onClickBody={() =>
                              navigate({
                                to: hivesOverviewOrganizationDetailsRoute.to,
                                params: {
                                  orgId: org.org_id,
                                },
                              })
                            }
                          >
                            {org.org_id}
                          </DataTag>
                        </Table.Cell>
                      </Table.Row>
                      {org.org_name && (
                        <Table.Row>
                          <Table.Cell>
                            {t('accountManagement.organizations.orgName')}
                          </Table.Cell>
                          <Table.Cell>
                            <DataTag
                              fontWeight={500}
                              fontSize="1rem"
                              value={org.org_name}
                            >
                              {org.org_name}
                            </DataTag>
                          </Table.Cell>
                        </Table.Row>
                      )}
                      {org.owner_id && (
                        <Table.Row>
                          <Table.Cell>
                            {t('accountManagement.organizations.ownerId')}
                          </Table.Cell>
                          <Table.Cell>
                            <DataTag
                              fontWeight={500}
                              fontSize="1rem"
                              value={org.owner_id}
                            >
                              {org.owner_id}
                            </DataTag>
                          </Table.Cell>
                        </Table.Row>
                      )}
                      {org.owner_id && (
                        <Table.Row>
                          <Table.Cell>
                            {t('accountManagement.account.isOwner.label')}
                          </Table.Cell>
                          <Table.Cell>
                            <DataTag
                              fontWeight={500}
                              fontSize="1rem"
                              hasCopy={false}
                              colorPalette={
                                org.owner_id === user.id ? 'green' : 'red'
                              }
                              value={
                                org.owner_id === user.id
                                  ? String(
                                      t(
                                        'accountManagement.account.isOwner.yes',
                                      ),
                                    )
                                  : String(
                                      t('accountManagement.account.isOwner.no'),
                                    )
                              }
                            >
                              {org.owner_id === user.id
                                ? t('accountManagement.account.isOwner.yes')
                                : t('accountManagement.account.isOwner.no')}
                            </DataTag>
                          </Table.Cell>
                        </Table.Row>
                      )}
                    </React.Fragment>
                  ))}
                </Table.Body>
              </Table.Root>
            ) : (
              <Text color="gray.500">
                {t('accountManagement.organizations.noOrganizations')}
              </Text>
            )}
          </SectionCard>
        </GridItem>
      </Grid>
    </SectionCard>
  )
}

function getAuthMethodByUserId(userId?: string): AuthMethod | null {
  if (!userId) {
    return null
  }

  const id = userId.toLowerCase()

  if (id.startsWith('google')) {
    return AuthMethod.Google
  }
  if (id.startsWith('github')) {
    return AuthMethod.Github
  }
  if (id.startsWith('linked')) {
    return AuthMethod.LinkedIn
  }
  return AuthMethod.UsernameAndPassword
}

function getAuthMethodDisplayValue(
  authMethod: AuthMethod | null,
  t: (key: string) => string,
): string {
  if (!authMethod) {
    return t('accountManagement.account.authMethod.notAvailable')
  }

  return {
    [AuthMethod.Github]: t('accountManagement.account.authMethod.github'),
    [AuthMethod.Google]: t('accountManagement.account.authMethod.google'),
    [AuthMethod.LinkedIn]: t('accountManagement.account.authMethod.linkedin'),
    [AuthMethod.UsernameAndPassword]: t(
      'accountManagement.account.authMethod.usernameAndPassword',
    ),
  }[authMethod]
}
